import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, PackageCheck, FileEdit, Clock, Banknote } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { fetchConsoleOrders, formatFcfa, type ConsoleOrderRow } from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA order lifecycle console (Epic 3) — the operations view over the order
// pipeline: from Brouillon through Confirmée / Réservée / Emballée / Chargée /
// En route to Livrée (or Échouée / Retournée). All reads are RLS-scoped
// (migrations 131-140); a non-authorized JWT simply reads zero rows.
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  confirmed: "Confirmée",
  reserved: "Réservée",
  packed: "Emballée",
  loaded: "Chargée",
  in_transit: "En route",
  delivered: "Livrée",
  failed: "Échouée",
  returned: "Retournée",
};

const CHANNEL_LABELS: Record<string, string> = {
  app: "App",
  whatsapp_text: "WhatsApp",
  whatsapp_voice: "WhatsApp vocal",
  agent: "Agent",
  call_center: "Centre d'appel",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  paid: "Payée",
  failed: "Échec",
  partial: "Partiel",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function OrdersBoard() {
  const q = useQuery({ queryKey: ["djayma", "console-orders"], queryFn: () => fetchConsoleOrders(100), retry: false });

  const rows: ConsoleOrderRow[] = q.data ?? [];
  const totalOrders = rows.length;
  const draftCount = rows.filter((r) => r.status === "draft").length;
  const pendingPaymentCount = rows.filter((r) => r.payment_status === "pending").length;
  const totalAmount = rows.reduce((acc, r) => acc + Number(r.total_xof ?? 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <ClipboardList className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Commandes — cycle de vie</h1>
            <p className="text-sm text-stone-500">
              Suivi du pipeline des commandes — du brouillon à la livraison
            </p>
          </div>
        </div>

        {q.isError && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Commandes non joignables — accès restreint par RLS. Vérifiez les migrations 131-140 et l&apos;exposition
            du schéma <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi icon={<ClipboardList className="h-5 w-5" />} tone="bg-amber-100 text-amber-700"
               label="Total commandes" value={totalOrders} />
          <Kpi icon={<FileEdit className="h-5 w-5" />} tone="bg-stone-100 text-stone-700"
               label="Brouillons" value={draftCount} />
          <Kpi icon={<Clock className="h-5 w-5" />} tone="bg-rose-100 text-rose-700"
               label="Paiements en attente" value={pendingPaymentCount} />
          <Kpi icon={<Banknote className="h-5 w-5" />} tone="bg-emerald-100 text-emerald-700"
               label="Montant total" value={formatFcfa(totalAmount)} />
        </div>

        {/* Orders table */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-700">
            <PackageCheck className="h-4 w-4 text-stone-400" />
            Commandes récentes
          </h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune commande.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Boutique</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 pr-4 font-medium">Canal</th>
                    <th className="py-2 pr-4 font-medium">Paiement</th>
                    <th className="py-2 pr-4 font-medium text-right">Articles</th>
                    <th className="py-2 pr-4 font-medium text-right">Montant</th>
                    <th className="py-2 pr-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="py-2 pr-4">
                        <div className="text-stone-700">{r.shop_name}</div>
                        <div className="text-xs text-stone-400">
                          {r.zone} · {r.region}
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-stone-500">{STATUS_LABELS[r.status] ?? r.status}</td>
                      <td className="py-2 pr-4 text-stone-500">{CHANNEL_LABELS[r.channel] ?? r.channel}</td>
                      <td className="py-2 pr-4 text-stone-500">
                        {PAYMENT_STATUS_LABELS[r.payment_status] ?? r.payment_status}
                      </td>
                      <td className="py-2 pr-4 text-right text-stone-500">{r.item_count}</td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(r.total_xof)}</td>
                      <td className="py-2 pr-4 text-stone-500">
                        {new Date(r.created_at).toLocaleDateString("fr-FR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
